import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Maximize2, Download, Sparkles, Heart, ArrowLeft, Wand2 } from 'lucide-react';
import { useT } from '../studioI18n';
import { useProject } from '../ProjectContext';
import { fileUrl } from '../studioApi';
import { Page, MotionButton, stagger, rise } from '../components/Motion';
import { BeforeAfter } from '../components/BeforeAfter';
import { FullscreenViewer } from '../components/FullscreenViewer';

const Progress = ({ job }) => {
  const { t } = useT();
  const [i, setI] = useState(0);
  useEffect(() => { const id = setInterval(() => setI((x) => (x + 1) % t.rvMsgs.length), 3200); return () => clearInterval(id); }, [t.rvMsgs.length]);
  const pct = Math.max(6, Math.round(((job.done_count + job.failed_count) / Math.max(1, job.total)) * 100));
  return (
    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="st-glass-strong rounded-[1.5rem] p-4 sm:p-5" data-testid="generation-progress-card">
      <div className="flex items-center gap-3">
        <span className="relative h-11 w-11 rounded-2xl bg-teal-600 text-white flex items-center justify-center shadow-lg shadow-teal-600/30 st-ring"><Wand2 className="h-5 w-5" /></span>
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-slate-900">{t.rvGenerating}</p>
          <AnimatePresence mode="wait"><motion.p key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} className="text-xs text-slate-600 truncate" data-testid="generation-status-message">{t.rvMsgs[i]}</motion.p></AnimatePresence>
        </div>
        <span className="text-sm font-extrabold text-teal-700 tabular-nums" data-testid="generation-percentage-text">{pct}%</span>
      </div>
      <div className="mt-3 h-2.5 rounded-full bg-slate-200/80 overflow-hidden"><motion.div className="h-full bg-teal-600 st-stripes" animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }} /></div>
      <p className="text-[11px] text-slate-500 mt-1.5">{t.rvProgress(job.done_count, job.total)}</p>
    </motion.div>
  );
};

const label = (v, t) => `${t.styles[v.window_style] || v.window_style}${v.grille && v.grille !== 'none' ? ` · ${t.grilles[v.grille]}` : ''}`;

export const RevealStep = () => {
  const { t } = useT();
  const { project, variantsByPhoto, running } = useProject();
  const navigate = useNavigate();
  const photos = project.photos;
  const [pid, setPid] = useState(photos[0]?.id);
  const [vid, setVid] = useState(null);
  const [full, setFull] = useState(false);
  const photo = photos.find((p) => p.id === pid) || photos[0];
  const variants = useMemo(() => (variantsByPhoto[photo?.id] || []), [variantsByPhoto, photo]);
  const current = variants.find((v) => v.id === vid) || variants[0];
  useEffect(() => { if (variants.length && !variants.some((v) => v.id === vid)) setVid(variants[0].id); }, [variants, vid]);
  const requested = project.lead.stage === 'estimate_requested';
  const aw = photo?.width || 4, ah = photo?.height || 3;
  const aspect = `${aw} / ${ah}`;
  // nunca maior que a tela: largura = min(100%, altura útil × proporção)
  const fit = { width: `min(100%, calc(68dvh * ${(aw / ah).toFixed(4)}))`, margin: '0 auto' };

  return (
    <Page testId="reveal-step">
      <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
        <motion.div variants={rise} className="text-center max-w-xl mx-auto">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900">{t.rvTitle}</h1>
          <p className="text-slate-600 mt-2">{t.rvSub}</p>
        </motion.div>
        <AnimatePresence>{running && <Progress job={running} />}</AnimatePresence>

        {photos.length > 1 && (
          <motion.div variants={rise} className="flex gap-2 overflow-x-auto st-scroll pb-1" data-testid="reveal-photo-nav">
            {photos.map((p, i) => (
              <motion.button key={p.id} whileTap={{ scale: 0.95 }} type="button" onClick={() => setPid(p.id)} className={`relative shrink-0 h-16 w-24 rounded-2xl overflow-hidden border-2 transition-colors ${p.id === photo.id ? 'border-teal-600' : 'border-transparent opacity-70'}`} data-testid={`reveal-photo-${i}`}>
                <img src={fileUrl(p.id, 'thumb')} alt="" className="h-full w-full object-cover" />
                {(variantsByPhoto[p.id] || []).length > 0 && <span className="absolute bottom-1 right-1 h-5 min-w-5 px-1 rounded-full bg-teal-600 text-white text-[10px] font-bold flex items-center justify-center">{variantsByPhoto[p.id].length}</span>}
              </motion.button>
            ))}
          </motion.div>
        )}

        <motion.div variants={rise} className="rounded-[2rem] overflow-hidden shadow-2xl ring-1 ring-white/70 bg-slate-900 relative" style={fit} data-testid="reveal-stage">
          <AnimatePresence mode="wait">
            {current ? (
              <motion.div key={current.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} className="st-wipe">
                <BeforeAfter before={fileUrl(photo.id)} after={fileUrl(current.file_id)} beforeThumb={fileUrl(photo.id, 'thumb')} afterThumb={fileUrl(current.file_id, 'thumb')} labels={{ before: t.before, after: t.after }} note={t.illustrative} aspect={aspect} testId="before-after-slider-0">
                  <div className="absolute top-12 right-3 flex gap-2" onPointerDown={(e) => e.stopPropagation()}>
                    <a href={fileUrl(current.file_id)} download target="_blank" rel="noreferrer" className="h-10 w-10 rounded-full bg-white/90 text-slate-800 flex items-center justify-center shadow" aria-label={t.download} data-testid="download-render-btn"><Download className="h-4 w-4" /></a>
                    <button type="button" onClick={() => setFull(true)} className="h-10 w-10 rounded-full bg-white/90 text-slate-800 flex items-center justify-center shadow" aria-label={t.fullscreen} data-testid="fullscreen-zoom-btn-0"><Maximize2 className="h-4 w-4" /></button>
                  </div>
                </BeforeAfter>
              </motion.div>
            ) : (
              <motion.div key="skeleton" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="relative st-shimmer" style={{ aspectRatio: aspect }} data-testid="reveal-skeleton">
                <img src={fileUrl(photo.id)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-35" />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center p-6">
                  <span className="relative h-14 w-14 rounded-full bg-teal-600 text-white flex items-center justify-center shadow-xl st-ring"><Sparkles className="h-6 w-6" /></span>
                  <p className="font-display font-bold text-slate-900">{running ? t.rvGenerating : t.noVariantsYet}</p>
                  {!running && <MotionButton variant="dark" className="h-11 text-sm" onClick={() => navigate('/project/design')} data-testid="reveal-go-design">{t.tryAnother}</MotionButton>}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {variants.length > 0 && (
          <motion.div variants={rise} className="flex flex-wrap items-center gap-2" data-testid="variant-pills">
            <span className="text-xs font-bold text-slate-500 mr-1">{t.variants}:</span>
            {variants.map((v, i) => (
              <motion.button key={v.id} whileTap={{ scale: 0.95 }} type="button" onClick={() => setVid(v.id)} aria-pressed={v.id === current?.id} className={`inline-flex items-center gap-2 h-10 px-3 rounded-full border text-xs font-bold transition-all ${v.id === current?.id ? 'bg-slate-900 text-white border-slate-900 shadow-lg' : 'bg-white/80 text-slate-700 border-slate-200 hover:border-teal-300'}`} data-testid={`variant-pill-${i}`}>
                <span className="h-4 w-4 rounded-full border border-black/10" style={{ background: v.color?.hex }} /> {v.color?.name} <span className={v.id === current?.id ? 'text-white/70' : 'text-slate-400'}>· {label(v, t)}</span>
              </motion.button>
            ))}
          </motion.div>
        )}
      </motion.div>

      <div className="fixed bottom-0 inset-x-0 z-30 p-3 sm:p-4 bg-white/80 backdrop-blur-xl border-t border-white/80" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <button type="button" onClick={() => navigate('/project/design')} className="inline-flex items-center gap-1 text-sm font-bold text-slate-600 hover:text-slate-900 shrink-0" data-testid="reveal-try-another-btn"><ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">{t.tryAnother}</span><span className="sm:hidden">{t.back}</span></button>
          <MotionButton onClick={() => navigate('/project/estimate')} className="px-6" data-testid="request-estimate-cta-btn"><Heart className="h-5 w-5" /> {requested ? t.estimateCard : t.loveIt}</MotionButton>
        </div>
      </div>

      <AnimatePresence>
        {full && current && (
          <FullscreenViewer open onClose={() => setFull(false)} aspectW={aw} aspectH={ah} before={fileUrl(photo.id)} after={fileUrl(current.file_id)} beforeThumb={fileUrl(photo.id, 'thumb')} afterThumb={fileUrl(current.file_id, 'thumb')}
            labels={{ before: t.before, after: t.after }} note={t.illustrative} zoomLabels={{ in: t.zoomIn, out: t.zoomOut, fit: t.zoomFit, close: t.close }} />
        )}
      </AnimatePresence>
    </Page>
  );
};
